/**
 * Static option lists and default filter states for the Todo (Task) feature.
 * Shared by the List and Grid views and the filter panel.
 */
import type { DateRangeFilter, Option } from "../types";

/** Tag options for the filter panel. */
export const TAG_OPTIONS: Option[] = [
  { value: "All Tags", label: "All Tags" },
  { value: "Social", label: "Social" },
  { value: "Meetings", label: "Meetings" },
  { value: "Research", label: "Research" },
  { value: "Reminder", label: "Reminder" },
  { value: "Projects", label: "Projects" },
  { value: "Internal", label: "Internal" },
];

/** Stage/status options (includes the synthetic "overdue" pseudo-status). */
export const STATUS_OPTIONS: Option[] = [
  { value: "All", label: "All Statuses" },
  { value: "0", label: "Pending" },
  { value: "1", label: "Inprogress" },
  { value: "2", label: "Onhold" },
  { value: "3", label: "Completed" },
  { value: "overdue", label: "Overdue" },
];

/** Recurring filter options. */
export const RECURRING_OPTIONS: Option[] = [
  { value: "All", label: "All Recurring" },
  { value: "Recurring", label: "Recurring" },
  { value: "Non-Recurring", label: "Non-Recurring" },
];

/**
 * Priority options. The leading "all" entry is used by the Grid view's priority
 * tabs; the List view only ever looks up by numeric value, so the extra entry is
 * harmless there.
 */
export const PRIORITY_OPTIONS: Option[] = [
  { value: "all", label: "All" },
  { value: "0", label: "High" },
  { value: "1", label: "Medium" },
  { value: "2", label: "Low" },
];

/** Stage options used by the inline stage dropdown / completion controls. */
export const STAGE_OPTIONS: Option[] = [
  { value: "0", label: "Pending" },
  { value: "1", label: "Inprogress" },
  { value: "2", label: "Onhold" },
  { value: "3", label: "Completed" },
];

/** Sort options (Grid view). */
export const SORT_OPTIONS: Option[] = [
  { value: "Created Date", label: "Created Date" },
  { value: "Priority", label: "Priority" },
  { value: "Due Date", label: "Due Date" },
  { value: "Recurring", label: "Recurring" },
  { value: "Non-Recurring", label: "Non-Recurring" },
];

/** Default filter state for the List view. */
export interface ListFilterState {
  selectedTag: string;
  selectedAssignee: string;
  selectedFollower: string;
  selectedStatus: string;
  selectedRecurring: string;
  selectedReporter: string;
  dueDateFilter: DateRangeFilter | null;
  createDateFilter: DateRangeFilter | null;
  sortBy: string;
}

export const LIST_INITIAL_FILTERS: ListFilterState = {
  selectedTag: "All Tags",
  selectedAssignee: "All",
  selectedFollower: "All",
  selectedStatus: "0",
  selectedRecurring: "All",
  selectedReporter: "All Reporters",
  dueDateFilter: null,
  createDateFilter: null,
  sortBy: "Last 7 Days",
};

/** Default filter state for the Grid view. */
export interface GridFilterState {
  selectedCompanyId: string | null;
  selectedPriority: string;
  selectedTag: string;
  selectedStatus: string;
  selectedRecurring: string;
  selectedAssignee: string;
  selectedFollower: string;
  selectedReporter: string;
  sortBy: string;
  dueDateFilter: DateRangeFilter | null;
  createDateFilter: DateRangeFilter | null;
}

export const GRID_INITIAL_FILTERS: GridFilterState = {
  selectedCompanyId: null,
  selectedPriority: "all",
  selectedTag: "All Tags",
  selectedStatus: "All",
  selectedAssignee: "All Assignees",
  selectedFollower: "All Follower",
  selectedRecurring: "All",
  selectedReporter: "All Reporters",
  sortBy: "Priority",
  dueDateFilter: null,
  createDateFilter: null,
};
